import { makeStyles } from "@material-ui/core";

/**
 * � Estilos WhatsApp Web 2.0 + Microinterações
 * 
 * Melhorias implementadas:
 * - Visual moderno tipo WhatsApp Web 2024
 * - Gradientes sutis e elegantes
 * - Sombras com blur moderno
 * - Animações fluidas (bounce, fade, etc.)
 * - Microinterações responsivas
 * - Dark mode otimizado
 */
export const useMessageBubbleStyles = makeStyles((theme) => ({  // 🎨 Keyframes para animações
  '@keyframes messageSlideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px) scale(0.95)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
  
  '@keyframes reactionBounce': {
    '0%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.2)' },
    '100%': { transform: 'scale(1)' },
  },
  
  '@keyframes reactionPulse': {
    '0%': { 
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(0, 150, 255, 0.4)',
    },
    '70%': { 
      transform: 'scale(1.05)',
      boxShadow: '0 0 0 8px rgba(0, 150, 255, 0)',
    },
    '100%': { 
      transform: 'scale(1)',
      boxShadow: '0 0 0 0 rgba(0, 150, 255, 0)',
    },
  },
  
  '@keyframes rippleEffect': {
    '0%': {
      transform: 'scale(0)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(4)',
      opacity: 0,
    },
  },

  '@keyframes typingDots': {
    '0%, 60%, 100%': {
      transform: 'translateY(0)',
    },
    '30%': {
      transform: 'translateY(-10px)',
    },
  },

  '@keyframes floatUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px) scale(0.8)',
    },
    '50%': {
      opacity: 1,
      transform: 'translateY(-5px) scale(1.05)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
    '@keyframes fadeInUp': {
    '0%': {
      opacity: 0,
      transform: 'translateY(20px)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0)',
    },
  },

  '@keyframes reactionClickBounce': {
    '0%': { 
      transform: 'scale(1) translateY(0)' 
    },
    '30%': { 
      transform: 'scale(1.25) translateY(-4px)' 
    },
    '60%': { 
      transform: 'scale(0.95) translateY(2px)' 
    },
    '100%': { 
      transform: 'scale(1) translateY(0)' 
    },
  },

  '@keyframes reactionShimmer': {
    '0%': {
      backgroundPosition: '-200% 0',
    },
    '100%': {
      backgroundPosition: '200% 0',
    },
  },// 🎯 Container base para indicadores com animação
  forwardedIndicator: {
    marginBottom: theme.spacing(0.5),
    animation: '$fadeInUp 0.3s ease-out',
    "& br": {
      display: "none", // Remove quebra de linha desnecessária
    },
  },

  youtubePreviewContainer: {
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1.5),
    overflow: "hidden",
    boxShadow: theme.mode === "light" 
      ? "0 4px 12px rgba(0, 0, 0, 0.1)" 
      : "0 4px 12px rgba(0, 0, 0, 0.4)",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    animation: '$fadeInUp 0.4s ease-out',
    
    "&:hover": {
      transform: "scale(1.02) translateY(-2px)",
      boxShadow: theme.mode === "light" 
        ? "0 8px 25px rgba(0, 0, 0, 0.15)" 
        : "0 8px 25px rgba(0, 0, 0, 0.5)",
    },
  },

  deletedMessageIndicator: {
    marginBottom: theme.spacing(0.5),
    animation: '$fadeInUp 0.3s ease-out',
  },

  mediaContainer: {
    marginBottom: theme.spacing(1),
    borderRadius: theme.spacing(1.5),
    overflow: "hidden",
    position: "relative",
    animation: '$fadeInUp 0.4s ease-out',
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    
    // Efeito de hover mais suave
    "&:hover": {
      transform: "scale(1.02) translateY(-1px)",
      boxShadow: theme.mode === "light" 
        ? "0 6px 20px rgba(0, 0, 0, 0.12)" 
        : "0 6px 20px rgba(0, 0, 0, 0.4)",
    },
    
    // Loading placeholder
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(90deg, 
        transparent, 
        ${theme.mode === "light" ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.1)"}, 
        transparent
      )`,
      transform: "translateX(-100%)",
      animation: "shimmer 1.5s infinite",
      pointerEvents: "none",
    },
  },

  messageBodyContainer: {
    wordBreak: "break-word",
    lineHeight: 1.5,
    animation: '$fadeInUp 0.3s ease-out',
    
    // Melhor tipografia
    "& p": {
      margin: 0,
      marginBottom: theme.spacing(0.5),
      
      "&:last-child": {
        marginBottom: 0,
      },
    },
    
    // Links com estilo melhorado
    "& a": {
      color: theme.mode === "light" ? "#1976d2" : "#64b5f6",
      textDecoration: "none",
      borderBottom: "1px solid transparent",
      transition: "all 0.2s ease",
      
      "&:hover": {
        borderBottom: `1px solid ${theme.mode === "light" ? "#1976d2" : "#64b5f6"}`,
      },
    },
  },
  xmlContent: {
    fontFamily: "Fira Code, Monaco, Consolas, monospace",
    backgroundColor: theme.mode === "light" 
      ? "rgba(0, 0, 0, 0.05)" 
      : "rgba(255, 255, 255, 0.08)",
    padding: theme.spacing(0.75, 1.25),
    borderRadius: theme.spacing(1),
    fontSize: "0.9em",
    border: `1px solid ${theme.mode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.15)"}`,
    backdropFilter: "blur(10px)",
    transition: "all 0.2s ease",
    animation: '$fadeInUp 0.3s ease-out',
    
    "&:hover": {
      backgroundColor: theme.mode === "light" 
        ? "rgba(0, 0, 0, 0.08)" 
        : "rgba(255, 255, 255, 0.12)",
    },
  },

  reactionMessageContainer: {
    fontStyle: "italic",
    opacity: 0.85,
    marginBottom: theme.spacing(0.5),
    animation: '$fadeInUp 0.3s ease-out',
    transition: "opacity 0.2s ease",
    
    "&:hover": {
      opacity: 1,
    },
  },

  reactionMessageText: {
    fontSize: "0.9em",
    color: theme.mode === "light" ? "#667781" : "#8696a0",  },  // 🎯 Sistema de reações WhatsApp oficial - SOBREPOSTA ao balão
  reactionAreaRight: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(-0.5), // NEGATIVO para sobreposição
    marginLeft: theme.spacing(4), // Alinhamento com a borda do balão
    marginBottom: theme.spacing(0.5),
    justifyContent: "flex-start",
    alignItems: "center",
    animation: '$fadeInUp 0.4s ease-out 0.1s both',
    position: "relative",
    zIndex: 10, // Para ficar acima do balão
  },

  reactionAreaLeft: {
    display: "flex",
    flexWrap: "wrap", 
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(-0.5), // NEGATIVO para sobreposição
    marginRight: theme.spacing(4), // Alinhamento com a borda do balão
    marginBottom: theme.spacing(0.5),
    justifyContent: "flex-end",
    alignItems: "center",
    animation: '$fadeInUp 0.4s ease-out 0.1s both',
    position: "relative",
    zIndex: 10, // Para ficar acima do balão
  },  // 🎯 Reações estilo WhatsApp oficial - com efeitos visuais completos
  reactionMsg: {
    fontSize: "14px",
    padding: theme.spacing(0.4, 0.8),
    borderRadius: theme.spacing(2.5),
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: theme.spacing(0.4),
    minWidth: "32px",
    height: "28px",
    fontWeight: 500,
    userSelect: "none",
    position: "relative",
    overflow: "hidden",
    
    // Visual limpo estilo WhatsApp
    background: theme.mode === "light" 
      ? "rgba(255, 255, 255, 0.95)"
      : "rgba(55, 55, 55, 0.95)",
    
    border: theme.mode === "light"
      ? "1px solid rgba(0, 0, 0, 0.1)"
      : "1px solid rgba(255, 255, 255, 0.15)",
    
    boxShadow: theme.mode === "light"
      ? "0 2px 8px rgba(0, 0, 0, 0.15)"
      : "0 2px 8px rgba(0, 0, 0, 0.4)",
    
    backdropFilter: "blur(10px)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    
    // 🎨 Efeitos de hover aprimorados
    "&:hover": {
      transform: "translateY(-2px) scale(1.1)",
      boxShadow: theme.mode === "light"
        ? "0 4px 16px rgba(0, 0, 0, 0.2)"
        : "0 4px 16px rgba(0, 0, 0, 0.5)",
      
      background: theme.mode === "light" 
        ? "rgba(255, 255, 255, 1)"
        : "rgba(65, 65, 65, 1)",
        
      border: theme.mode === "light"
        ? "1px solid rgba(0, 0, 0, 0.2)"
        : "1px solid rgba(255, 255, 255, 0.25)",
    },
    
    // 🎨 Efeito de clique
    "&:active": {
      transform: "translateY(0) scale(0.95)",
      transition: "transform 0.1s ease",
    },
  },

  // 🎯 Contador integrado dentro da reação (estilo WhatsApp oficial)
  reactionMsgCounter: {
    fontSize: "11px", // Tamanho menor para melhor integração
    fontWeight: 600,
    color: theme.mode === "light" ? "#667781" : "#8696a0",
    marginLeft: theme.spacing(0.3), // Espaço mínimo do emoji
    lineHeight: 1,
    // Não precisa de background próprio, está integrado na reação
  },

  // 🎯 Classe específica para animação de clique
  'reaction-bouncing': {
    animation: '$reactionClickBounce 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55) !important',
    transformOrigin: 'center center',
  },
  // 🌟 Efeito shimmer para reações ativas
  'reaction-shimmer': {
    position: 'relative',
    overflow: 'hidden',
    
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: `linear-gradient(90deg, 
        transparent, 
        rgba(255, 255, 255, 0.4), 
        transparent
      )`,
      backgroundSize: '200% 100%',
      animation: '$reactionShimmer 1s ease-in-out',
      pointerEvents: 'none',
      borderRadius: 'inherit',
    },
  },

  // 🎯 Efeito ripple para cliques
  'ripple-effect': {
    position: 'relative',
    overflow: 'hidden',
    
    '&::before': {
      content: '""',
      position: 'absolute',
      top: '50%',
      left: '50%',
      width: '0',
      height: '0',
      borderRadius: '50%',
      background: 'rgba(255, 255, 255, 0.6)',
      transformOrigin: 'center',
      transform: 'translate(-50%, -50%)',
      animation: '$rippleEffect 0.6s ease-out',
      pointerEvents: 'none',
    },
  },

  // 🎭 Indicador de digitação moderno
  typingIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    padding: theme.spacing(1, 1.5),
    backgroundColor: theme.mode === "light" ? "#f0f0f0" : "#2a2a2a",
    borderRadius: theme.spacing(2),
    margin: theme.spacing(0.5, 0),
    animation: '$floatUp 0.4s ease-out',
    
    '& .typing-dot': {
      width: 8,
      height: 8,
      borderRadius: '50%',
      backgroundColor: theme.mode === "light" ? "#999" : "#666",
      animation: '$typingDots 1.4s infinite ease-in-out',
      
      '&:nth-child(1)': { animationDelay: '0s' },
      '&:nth-child(2)': { animationDelay: '0.2s' },
      '&:nth-child(3)': { animationDelay: '0.4s' },
    },
  },

  // 🎨 Mensagem com entrada suave
  messageWithEntrance: {
    animation: '$messageSlideIn 0.3s ease-out',
  },

  // ✨ Hover especial para mensagens
  messageHoverEffect: {
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: theme.mode === "light"
        ? '0 4px 20px rgba(0, 0, 0, 0.08)'
        : '0 4px 20px rgba(0, 0, 0, 0.3)',
    },
  },

  // 💡 Modo seleção: destaque da mensagem
  messageSelected: {
    outline: `2px solid ${theme.palette.primary.main}`,
    boxShadow: theme.mode === "light"
      ? "0 6px 20px rgba(25, 118, 210, 0.2)"
      : "0 6px 20px rgba(100, 181, 246, 0.25)",
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      inset: 0,
      borderRadius: theme.spacing(2),
      background: theme.mode === 'light'
        ? 'rgba(25, 118, 210, 0.06)'
        : 'rgba(100, 181, 246, 0.10)',
      pointerEvents: 'none',
    },
  },

  messageSelectable: {
    cursor: 'pointer',
    transition: 'transform 0.12s ease, box-shadow 0.12s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: theme.mode === 'light'
        ? '0 4px 14px rgba(25, 118, 210, 0.18)'
        : '0 4px 14px rgba(100, 181, 246, 0.24)'
    }
  },

  reactionMsgActive: {
    background: `linear-gradient(135deg, 
      rgba(0, 150, 255, 0.15) 0%, 
      rgba(0, 123, 255, 0.25) 100%
    ) !important`,
    
    border: "1px solid rgba(0, 150, 255, 0.4) !important",
    
    boxShadow: `
      0 0 0 2px rgba(0, 150, 255, 0.1),
      0 4px 12px rgba(0, 150, 255, 0.2),
      0 2px 6px rgba(0, 0, 0, 0.1)
    !important`,
    
    animation: '$reactionPulse 0.6s ease-out',
    
    "&:hover": {
      background: `linear-gradient(135deg, 
        rgba(0, 150, 255, 0.25) 0%, 
        rgba(0, 123, 255, 0.35) 100%
      ) !important`,
      
      boxShadow: `
        0 0 0 3px rgba(0, 150, 255, 0.15),
        0 6px 20px rgba(0, 150, 255, 0.25),
        0 3px 8px rgba(0, 0, 0, 0.15)
      !important`,
    },
  },  reactionCounter: {
    fontSize: "12px", // Menor
    color: theme.mode === "light" ? "#667781" : "#8696a0",
    fontWeight: 600,
    cursor: "pointer",
    padding: theme.spacing(0.4, 0.8), // Mais compacto
    borderRadius: theme.spacing(2.5), // Mais arredondado
    minWidth: "24px", // Menor
    height: "24px", // Menor
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    
    background: theme.mode === "light" 
      ? "rgba(240, 242, 247, 0.9)"
      : "rgba(50, 50, 50, 0.9)",
    
    border: theme.mode === "light"
      ? "1px solid rgba(0, 0, 0, 0.08)"
      : "1px solid rgba(255, 255, 255, 0.12)",
    
    boxShadow: theme.mode === "light"
      ? "0 1px 3px rgba(0, 0, 0, 0.1)"
      : "0 1px 3px rgba(0, 0, 0, 0.3)",
    
    backdropFilter: "blur(10px)",
    transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
    
    "&:hover": {
      transform: "translateY(-1px) scale(1.05)",
      color: theme.mode === "light" ? "#1976d2" : "#64b5f6",
      background: theme.mode === "light" ? "rgba(255, 255, 255, 1)" : "rgba(60, 60, 60, 1)",
      boxShadow: theme.mode === "light"
        ? "0 2px 8px rgba(0, 0, 0, 0.12)"
        : "0 2px 8px rgba(0, 0, 0, 0.35)",
    },
    
    "&:active": {
      transform: "translateY(0) scale(0.95)",
    },
  },
  // 🕒 Timestamp modernizado - SEM espaços extras
  timestamp: {
    fontSize: "11px",
    color: theme.mode === "light" ? "#667781" : "#ffffff",
    marginTop: "2px", // REDUZIDO: espaço mínimo
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
    fontWeight: 500,
    letterSpacing: "0.01em",
    animation: '$fadeInUp 0.3s ease-out 0.2s both',
    
    // Melhor legibilidade com backdrop
    textShadow: theme.mode === "dark" 
      ? "0 1px 3px rgba(0, 0, 0, 0.8), 0 1px 2px rgba(0, 0, 0, 0.6)" 
      : "0 1px 2px rgba(255, 255, 255, 0.8)",
    
    // Efeito sutil no hover
    transition: "all 0.2s ease",
    "&:hover": {
      color: theme.mode === "light" ? "#333" : "#fff",
    },
  },
  // 💫 Estilos para mensagens deletadas
  textContentItemDeleted: {
    opacity: 0.6,
    fontStyle: "italic",
    filter: "grayscale(0.3)",
    transition: "all 0.3s ease",
    
    "&:hover": {
      opacity: 0.8,
      filter: "grayscale(0.1)",
    },
  },

  deletedMessage: {
    color: theme.mode === "light" ? "#667781" : "#8696a0",
    fontSize: "0.9em",
    fontStyle: "italic",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.5),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.mode === "light" 
      ? "rgba(255, 193, 7, 0.1)" 
      : "rgba(255, 193, 7, 0.15)",
    border: `1px solid ${theme.mode === "light" ? "rgba(255, 193, 7, 0.2)" : "rgba(255, 193, 7, 0.25)"}`,
    animation: '$fadeInUp 0.3s ease-out',
  },

  // 👥 Nome do contato em grupos (modernizado)
  messageContactName: {
    fontSize: "13px",
    fontWeight: 700,
    marginBottom: theme.spacing(0.5),
    display: "block",
    letterSpacing: "0.01em",
    
    // Gradiente colorido para nomes
    background: theme.mode === "light" 
      ? "linear-gradient(135deg, #00897b 0%, #4dd0e1 100%)"
      : "linear-gradient(135deg, #4dd0e1 0%, #26c6da 100%)",
    
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
    
    // Fallback para browsers que não suportam
    color: theme.mode === "light" ? "#00897b" : "#4dd0e1",
    
    textShadow: theme.mode === "dark" 
      ? "0 1px 3px rgba(0, 0, 0, 0.5)" 
      : "0 1px 2px rgba(0, 0, 0, 0.1)",
    
    transition: "all 0.2s ease",
    animation: '$fadeInUp 0.3s ease-out',
    
    "&:hover": {
      transform: "translateX(2px)",
    },
  },

  // 📤 Mensagem encaminhada (melhorada)
  forwardMessage: {
    fontSize: "13px",
    color: theme.mode === "light" ? "#667781" : "#8696a0",
    fontStyle: "italic",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.25, 0),
    transition: "all 0.2s ease",
    animation: '$fadeInUp 0.3s ease-out',
    
    "& svg": {
      transition: "transform 0.2s ease",
    },
    
    "&:hover": {
      color: theme.mode === "light" ? "#555" : "#aaa",
      
      "& svg": {
        transform: "translateX(-2px)",
      },
    },
  },

  // 🎭 Shimmer animation para loading
  '@keyframes shimmer': {
    '0%': { transform: 'translateX(-100%)' },
    '100%': { transform: 'translateX(100%)' },
  },
}));
